import {ErrorResponseSchema, type FailedResponse} from './types'
import {AppEnv, CREDENTIAL_POLICY} from '../conf/appEnv'
import {MediaType} from '../constants/MediaType'
import {Localization} from '../utils/Localization'

const GENERIC_NETWORK_ERROR = Error('Network Error')

export const UnknownNetworkErrorCode = -1
export const UnauthorizedErrorCode = 401
export const ForbiddenErrorCode = 403 // Error code when 'useAuthorized' condition is not met
export const BadRequestErrorCode = 400
export const NotFoundErrorCode = 404
export const InternalServerErrorCode = 500
export const ServiceUnavailableErrorCode = 503

/**
 * Api Utilities.
 */
export const ApiUtil = {
  doGetRequest,
  doPostRequest,
  doPutRequest,
  errorResponse,
  getApiUrl,
  resolveLocalizedErrorMessage,
  unknownExceptionResponse
}


/**
 * Execute 'POST' request.
 * @param apiUrl full API url.
 * @param body request body.
 * @param token authentication token.
 * @param customHeaders Optional: custom headers to include (or to override default headers)
 */
async function doPostRequest(
  apiUrl: string,
  body: Record<string, unknown>,
  token: string,
  customHeaders: Record<string, string | undefined> = {}): Promise<Response> {
  const requestOptions = createPostRequestOpts(body, token, customHeaders)
  return await handleRequest(async () => await fetch(apiUrl, requestOptions))
}

/**
 * Execute 'PUT' request.
 * @param apiUrl full API url.
 * @param body request body.
 * @param token authentication token.
 */
async function doPutRequest(apiUrl: string, body: Record<string, unknown>, token: string): Promise<Response> {
  const requestOptions = createPutRequestOpts(body, token)
  return await handleRequest(async () => await fetch(apiUrl, requestOptions))
}

/**
 * Execute 'GET' request.
 * @param apiUrl full API url.
 * @param token authentication token.
 */
async function doGetRequest(apiUrl: string, token: string): Promise<Response> {
  const requestOptions = createGetRequestOpts(token)
  return await handleRequest(async () => await fetch(apiUrl, requestOptions))
}

/**
 * Get full API url for API path.
 * @param apiPath
 */
function getApiUrl(apiPath: string): string {
  const baseUrl = AppEnv.getBackendBaseUrl()
  return `${baseUrl}/api${apiPath}`
}

/**
 * Produce API call failure response.
 * @param response Fetch-API response object.
 * @param apiDesc API description (for console logging purposes).
 */
async function errorResponse(response: Response, apiDesc: string): Promise<FailedResponse> {
  const {status: statusCode} = response

  console.warn(`Error response for ${apiDesc}: `, statusCode)
  let key: string | undefined = undefined
  if (statusCode === BadRequestErrorCode || statusCode === NotFoundErrorCode) {
    // find key
    key = await tryGetErrorKey(response)
  }

  return {
    succeeded: false,
    errorKey: key,
    errStatusCode: statusCode
  }
}


/**
 * Produce API call failure response for case when an exception is thrown.
 * @param ex thrown error/exception.
 * @param url full API url.
 * @param apiDesc API description (for console logging purposes).
 * @param body Optional: request body.
 */
function unknownExceptionResponse(ex: unknown, url: string, apiDesc: string, body: Record<string, unknown> = {}): FailedResponse {
  if (!body) {
    console.warn(`Error occurred on execution of ${apiDesc}: `, ex, ` (apiUrl: ${url})`)
  } else {
    console.warn(`Error occurred on execution of ${apiDesc}: `, ex, ` (apiUrl: ${url}, body: ${JSON.stringify(body)})`)
  }

  return {
    succeeded: false,
    errorKey: undefined,
    errStatusCode: UnknownNetworkErrorCode
  }
}

/**
 * Resolve localized error message for failed API response.
 * @param res failed response structure.
 */
function resolveLocalizedErrorMessage(res: FailedResponse): string {
  const {defaultLang: lang} = AppEnv
  if (res.errorKey) {
    // Prefer errors with error keys
    const localizedErrMsg = Localization.getLocalizedErrorMessage(res.errorKey, lang)
    if (localizedErrMsg) {
      return localizedErrMsg
    }
  }

  return Localization.getLocalizedHttpError(res.errStatusCode, lang);
}

/* Helper Functions */

function createGetRequestOpts(token: string): RequestInit {
  return {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: MediaType.JSON
    },
    credentials: CREDENTIAL_POLICY
  }
}

function createPostRequestOpts(body: Record<string, unknown>, token: string, customHeaders: Record<string, string | undefined> = {}): RequestInit {
  return {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: MediaType.JSON,
      'Content-Type': MediaType.JSON,
      ...customHeaders
    },
    credentials: CREDENTIAL_POLICY,
    body: JSON.stringify(body)
  }
}

function createPutRequestOpts(body: Record<string, unknown>, token: string): RequestInit {
  return {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: MediaType.JSON,
      'Content-Type': MediaType.JSON
    },
    credentials: CREDENTIAL_POLICY,
    body: JSON.stringify(body)
  }
}

async function handleRequest(networkCallFunc: () => Promise<Response>): Promise<Response> {
  try {
    return await networkCallFunc()
    // eslint-disable-next-line sonarjs/no-ignored-exceptions -- prefer known error messages
  } catch (_) {
    console.error('Network error occurred')
    throw GENERIC_NETWORK_ERROR
  }
}

async function tryGetErrorKey(errorRes: Response): Promise<string | undefined> {
  const payload: unknown = await errorRes.json()
  if (payload && typeof payload === 'object') {
    const errResponse = ErrorResponseSchema.safeParse(payload)
    if (errResponse.success) {
      return errResponse.data.key
    }
  }

  return undefined
}
